import { StyleSheet, Font } from "@react-pdf/renderer";
import OpenSansBold from "../../../../../assets/fonts/Open_Sans/OpenSans-Bold.ttf";
import OpenSansRegular from "../../../../../assets/fonts/Open_Sans/OpenSans-Regular.ttf";
import RobotoBold from "../../../../../assets/fonts/Roboto/Roboto-Bold.ttf";
import RobotoRegular from "../../../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoSemiBold from "../../../../../assets/fonts/Roboto/Roboto-SemiBold.ttf";
import RobotoItalic from "../../../../../assets/fonts/Roboto/Roboto-Italic.ttf";
import RalewayRegular from "../../../../../assets/fonts/Raleway/Raleway-Regular.ttf";
import RalewayBold from "../../../../../assets/fonts/Raleway/Raleway-Bold.ttf";

Font.register({
  family: "Open Sans",
  fonts: [
    { src: OpenSansBold, fontWeight: "bold" },
    { src: OpenSansRegular, fontWeight: "normal" },
  ],
});
Font.register({
  family: "Roboto",
  fonts: [
    { src: RobotoBold, fontWeight: "bold" },
    { src: RobotoRegular, fontWeight: "normal" },
    { src: RobotoSemiBold, fontWeight: "500" },
    { src: RobotoItalic, fontStyle: "italic" },
  ],
});

Font.register({
  family: "Raleway",
  fonts: [
    { src: RalewayRegular, fontWeight: "normal" },
    { src: RalewayBold, fontWeight: "bold" }, //  Explicitly defined
  ],
});

//  Helper to convert hex to RGB
const hexToRgb = (hex) => {
  const cleanHex = hex.replace("#", "");
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};

// Helper to calculate brightness
const getTextColor = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  // relative luminance formula
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 186 ? "#000000" : "#ffffff"; // threshold
};

const blendWithWhite10 = (hex, opacity = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const blended = {
    r: Math.round((1 - opacity) * 255 + opacity * r),
    g: Math.round((1 - opacity) * 255 + opacity * g),
    b: Math.round((1 - opacity) * 255 + opacity * b),
  };
  return `rgb(${blended.r}, ${blended.g}, ${blended.b})`;
};

const blendWithWhite = (hex, opacity = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const blended = {
    r: Math.round((1 - opacity) * 255 + opacity * r),
    g: Math.round((1 - opacity) * 255 + opacity * g),
    b: Math.round((1 - opacity) * 255 + opacity * b),
  };
  return `rgb(${blended.r}, ${blended.g}, ${blended.b})`;
};

const CfmReportStyles = (primaryColor = "081e56") => {
  const textColor = getTextColor(primaryColor);
  const highlightBg = blendWithWhite(primaryColor, 0.1);
  const headerfooterBg = blendWithWhite10(primaryColor, 0.3);

  return StyleSheet.create({
    headerTitle: {
      fontSize: 15,
      color: textColor,
      fontFamily: "Raleway",
      fontWeight: "bold",
      // textTransform: "uppercase",
    },
    headerTitle1: {
      fontSize: 16,
      color: textColor,
      fontFamily: "Open Sans",
      // textTransform: "uppercase",
    },

    page: {
      position: "relative",
      backgroundColor: "#ffffff",
      flexDirection: "column",
      // gap: 10,
    },
    graphicWrapper: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
    },
    graphicBox: {
      position: "absolute",
      top: "-92%",
      left: "-55%",
      width: "110%",
      height: "250%",
      backgroundColor: `#${primaryColor}`,
      transform: "rotate(45deg)",
    },
    graphicBox1: {
      position: "absolute",
      top: "-185%",
      right: "-87.5%",
      width: "110%",
      height: "250%",
      backgroundColor: highlightBg,
      transform: "rotate(35deg)",
    },
    graphicBox2: {
      position: "absolute",
      top: "78%",
      right: "-120%",
      width: "200%",
      height: "2%",
      backgroundColor: blendWithWhite10(primaryColor, 0.7),
      transform: "rotate(35deg)",
    },
    graphicBox3: {
      position: "absolute",
      top: "76%",
      right: "-120%",
      width: "200%",
      height: "2%",
      backgroundColor: blendWithWhite10(primaryColor, 0.5),
      transform: "rotate(35deg)",
    },
    bubblesWrapper: {
      position: "absolute",
      bottom: 25,
      right: 25,
      width: 120, // match outermost bubble width
      height: 120,
      justifyContent: "center",
      alignItems: "center",
      pointerEvents: "none",
    },

    bubbleOuter: {
      position: "absolute",
      right: -100,
      bottom: 30,
      width: 150,
      height: 150,
      borderRadius: 75,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.11,
    },

    bubbleMiddle: {
      position: "absolute",
      right: 5,
      bottom: 110,
      width: 110,
      height: 110,
      borderRadius: 55,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.13,
    },

    bubbleInner: {
      position: "absolute",
      right: 90,
      bottom: 90,
      width: 66,
      height: 66,
      borderRadius: 33,
      backgroundColor: `#${primaryColor}`,
      opacity: 0.17,
    },

    overlayContent: {
      position: "absolute",
      // top: 30,
      // left: 30,
      width: "70%",
      height: "100%",
      flexDirection: "column",
      // wordBreak: "none",
      // textOverflow: "word-wrap",
      gap: 20,
    },

    title: {
      fontSize: 36,
      fontWeight: "bold",
      fontFamily: "Roboto",
      color: textColor,
      // letterSpacing: 2,
      // marginBottom: 10,
      textTransform: "uppercase",
    },
    tradingNameTitle: {
      fontSize: 16,
      fontWeight: "500",
      fontFamily: "Roboto",
      color: textColor,
      // letterSpacing: 2,
      // marginBottom: 10,
      // textTransform: "uppercase",
    },
    tradingNameTitle1: {
      fontSize: 12,
      fontWeight: "200",
      lineHeight: "18px",
      fontFamily: "Roboto",
      color: textColor,
      // wordBreak: "none",
      // letterSpacing: 2,
      // marginBottom: 10,
      // textTransform: "uppercase",
    },
    disclaimerText: {
      fontSize: 10,
      fontFamily: "Roboto",
      fontStyle: "italic",
      color: textColor,
      textAlign: "justify",
    },
    disclaimerText1: {
      fontSize: 12,
      fontWeight: "400",
      fontFamily: "Open Sans",
      textAlign: "center",
      // marginTop: 10,
      // marginBottom: 10,
      // marginBottom: 30,
      // fontStyle: "italic",
      color: `#${primaryColor}`,
    },
    coverPageSubTitle: {
      fontSize: 10,
      lineHeight: "18px",
      fontWeight: "400",
      fontFamily: "Open Sans",
      color: textColor,
    },
    text: {
      fontSize: 12,
      fontWeight: "400",
      textAlign: "justify",
      fontFamily: "Open Sans",
      color: "#000000",
      lineHeight: "18px",
    },

    textBold: {
      fontSize: 12,
      fontWeight: "600",
      fontFamily: "Open Sans",
      color: `#${primaryColor}`,
      lineHeight: "18px",
    },
    textBold1: {
      fontSize: 12,
      fontWeight: "600",
      fontFamily: "Open Sans",
      color: "#000000",
      lineHeight: "18px",
    },
    logoContainer: {
      alignSelf: "flex-end",
      marginTop: 390,
      marginRight: 60,
    },
    logoImage: {
      maxWidth: 270,
      maxHeight: 150,

      // backgroundColor: `#${primaryColor}`,
    },

    tableHeader: {
      flexDirection: "row",
      fontSize: 10,
      fontFamily: "Open Sans",
      fontWeight: "normal",
      backgroundColor: `#${primaryColor}`,
      paddingVertical: 4,
      borderBottom: "0.5px solid #ccc",
      color: `#${primaryColor}`,
      height: 42,
    },
    ageCell: {
      textAlign: "center",
    },

    tableHeaderText: {
      flex: 1,
      paddingRight: 5,
      fontSize: 10,
      fontFamily: "Open Sans",
      alignSelf: "center",
      textAlign: "left",
      fontWeight: "bold",
      color: textColor, // Ensure header text is white for contrast
    },
    tableRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      fontSize: 8,
      lineHeight: "12px",
      fontFamily: "Open Sans",
      // paddingVertical: 2,
      height: 24,
      backgroundColor: "white",
      borderBottom: "0.5px solid #e0e0e0",
    },
    altRow: {
      backgroundColor: blendWithWhite(primaryColor, 0.05), // light grey for alternate rows
    },

    tableCell: {
      flex: 1,
      paddingRight: 5,
    },
    tableTitle: {
      fontSize: 12,
      fontWeight: "bold",
      fontFamily: "Open Sans",
      // marginTop: 10,
      marginBottom: 6,
      color: `#${primaryColor}`,
      textTransform: "capitalize",
    },
    section: {
      paddingLeft: 30,
      paddingRight: 30,
      paddingTop: 16,
      paddingBottom: 16,
      gap: "30px",
      // marginTop: 10,
    },

    highlightRow: {
      backgroundColor: highlightBg,
      fontWeight: "bold",
    },

    headerText: {
      fontSize: 12,
      fontFamily: "Open Sans",
      color: headerfooterBg,
      fontWeight: "800",
    },

    footer: {
      position: "absolute",
      bottom: 0,
      paddingLeft: 12,
      paddingRight: 12,
      textAlign: "center",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "white",
      width: "100%",
      height: 44,
    },

    footerText: {
      fontSize: 6,
      // lineHeight: "12px",
      fontWeight: "400",
      textAlign:"center",
      fontFamily: "Open Sans",
      color: blendWithWhite10(primaryColor, 0.8), // light grey
    },

    column: {
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: 12,
    },
    row: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      gap: "24px",
    },

    titleBarContainer: {
      height: 54,
      padding: "12px 24px",
      backgroundColor: `#${primaryColor}`,
    },

    titleBar: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      // padding: 6,
      // gap: 5,
    },

    subText: {
      fontSize: 12,
      color: textColor,
      fontStyle: "italic",
      lineHeight: "18px",
      // marginTop: 2,
    },
    subTitle: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "20px",
      fontFamily: "Raleway",
      color: `#${primaryColor}`,
      // textDecoration: "underline",
      textTransform: "capitalize",
    },
    chartImage: {
      // minWidth: "1500px",
      // maxHeight: "300px",
      // display: "flex",
      width: "90%",
      alignSelf: "center",
      // height: "75%",

      // objectFit: "contain",
      backgroundColor: "#ffffff",
    },
    graphNotesContainer: {
      // width: "100%",
      // height: "36px",
      // textAlign: "center",
      // alignItems: "center",
      // justifyContent: "center",
      // backgroundColor: blendWithWhite(primaryColor, 0.1),
      // borderRadius: 16,
      // borderLeft: `4px solid #${primaryColor}`,
      // borderRight: `4px solid #${primaryColor}`,
    },
    verticalSizeBox: {
      height: 20,
    },

    remarksContainer: {
        position: "absolute",
                        textAlign: "center",
                        bottom: 15,
                        left: 25, // match your page padding
                        right: 25,
    },
  });
};

export default CfmReportStyles;
